import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Home, Plus, Sparkles } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import { Button, Pill } from "@/components/kit";
import { StickyBar } from "@/components/layout";
import { api } from "@/lib/api";
import { useIsDesktop } from "@/lib/hooks";
import NewProjectDialog from "@/pages/NewProjectDialog";

const STATUS = {
  queued: { label: "Queued", kind: "info" },
  running: { label: "Rendering", kind: "info" },
  done: { label: "Video ready", kind: "success" },
  failed: { label: "Video failed", kind: "error" },
};

function ProjectCard({ p, index }) {
  const st = p.latest_video ? STATUS[p.latest_video.status] : null;
  const label = st && p.latest_video.status === "running" ? `${st.label} ${p.latest_video.progress}%` : st?.label;
  return (
    <Link to={`/projects/${p.id}`} data-testid={`project-card-${p.id}`} style={{ animationDelay: `${Math.min(index, 6) * 50}ms` }}
      className="focus-ring fade-up group relative block h-[230px] overflow-hidden rounded-[20px] border border-border bg-card transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/50 hover:shadow-[0_20px_40px_-20px_hsl(var(--primary)/0.35)]">
      {p.cover_url ? (
        <img src={p.cover_url} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-secondary"><Home className="h-10 w-10 text-muted-foreground" aria-hidden /></div>
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent from-25% via-background/90 via-80% to-background/90" />
      {label ? (
        <div className="absolute right-3 top-3"><Pill label={label} kind={st.kind} data-testid={`project-card-status-${p.id}`} /></div>
      ) : null}
      <div className="absolute inset-x-5 bottom-5 flex flex-col gap-1">
        <h2 className="line-clamp-2 text-xl font-medium text-foreground" data-testid={`project-card-address-${p.id}`}>{p.address}</h2>
        <p className="text-[13px] text-card-foreground" data-testid={`project-card-media-count-${p.id}`}>
          {p.media_count} {p.media_count === 1 ? "item" : "items"}{p.price ? `  ·  ${p.price}` : ""}
        </p>
      </div>
    </Link>
  );
}

function BrandingPrompt() {
  return (
    <Link to="/branding" data-testid="finish-branding-prompt"
      className="focus-ring mb-6 flex min-h-16 items-center gap-3 rounded-xl border border-accent bg-primary/15 p-4 transition-colors hover:bg-primary/20">
      <Sparkles className="h-6 w-6 shrink-0 text-primary" aria-hidden />
      <div className="flex-1">
        <p className="text-sm font-medium text-foreground">Finish your branding</p>
        <p className="text-[13px] text-muted-foreground">Add your name, phone and logo for the video end card.</p>
      </div>
      <ChevronRight className="h-5 w-5 text-muted-foreground" aria-hidden />
    </Link>
  );
}

function Empty() {
  return (
    <div className="flex flex-col items-center gap-3 px-6 pt-16 text-center" data-testid="projects-empty-state">
      <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/15"><Home className="h-9 w-9 text-primary" aria-hidden /></div>
      <h2 className="text-xl font-medium">No properties yet</h2>
      <p className="max-w-sm text-base text-card-foreground">Add a listing, upload your phone footage and get a branded video.</p>
    </div>
  );
}

export default function ProjectsPage({ creating }) {
  const navigate = useNavigate();
  const desktop = useIsDesktop();
  const projects = useQuery({
    queryKey: ["projects"],
    queryFn: () => api("/projects"),
    refetchInterval: (q) => (q.state.data?.some((p) => ["queued", "running"].includes(p.latest_video?.status ?? "")) ? 3000 : false),
  });
  const branding = useQuery({ queryKey: ["branding"], queryFn: () => api("/branding") });
  const list = projects.data ?? [];
  const empty = projects.data && list.length === 0;
  const newBtn = (
    <Button title="New property" icon={Plus} onClick={() => navigate("/projects/new")} className={desktop ? "" : "w-full"}
      data-testid={empty ? "empty-new-property-button" : "new-property-button"} />
  );

  return (
    <section data-testid="projects-screen">
      <header className="flex items-end gap-4 pb-6 lg:pb-8">
        <div className="flex-1">
          <p className="eyebrow mb-1">Your listings</p>
          <h1 className="text-[28px] font-medium tracking-tight lg:text-3xl">Properties</h1>
        </div>
        {desktop ? newBtn : null}
      </header>
      {branding.data && !branding.data.complete ? <BrandingPrompt /> : null}
      {projects.isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="projects-loading">
          {[0, 1, 2].map((i) => <div key={i} className="h-[230px] animate-pulse rounded-[20px] border border-border bg-card/60" />)}
        </div>
      ) : projects.isError && !projects.data ? (
        <div className="flex flex-col items-center gap-3 pt-16" data-testid="projects-error">
          <h2 className="text-xl font-medium">Failed to load projects</h2>
          <Button title="Retry" variant="secondary" compact onClick={() => projects.refetch()} data-testid="projects-retry-button" />
        </div>
      ) : empty ? (
        <Empty />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="projects-grid">
          {list.map((p, i) => <ProjectCard key={p.id} p={p} index={i} />)}
        </div>
      )}
      {!desktop ? <StickyBar>{newBtn}</StickyBar> : null}
      <NewProjectDialog open={!!creating} onClose={() => navigate("/projects", { replace: true })} />
    </section>
  );
}
